import React, { useEffect, useState } from 'react';
import { XIcon, DownloadIcon, CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { DiscordIcon } from './icons/DiscordIcon';

interface DiscordDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetryConnection: () => void;
}

export const DiscordDownloadModal: React.FC<DiscordDownloadModalProps> = ({
  isOpen,
  onClose,
  onRetryConnection
}) => {
  const [platform, setPlatform] = useState<'windows' | 'mac'>('windows');
  const [downloadStarted, setDownloadStarted] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('mac')) {
      setPlatform('mac');
    }
  }, []);

  if (!isOpen) return null;

  // For now, link to GitHub or instructions until installer is built
  // R2 CDN URLs for installers (ezart-assets bucket)
  const downloadUrl = platform === 'windows'
    ? 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/downloads/EZArt-Discord-Setup-1.0.0.exe'
    : 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/downloads/ezart-discord-companion-mac';

  const handleDownload = () => {
    // Check if file exists first
    fetch(downloadUrl, { method: 'HEAD' })
      .then(response => {
        if (response.ok && response.headers.get('content-type')?.includes('application')) {
          // File exists and is binary, download it
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = platform === 'windows' ? 'EZArt-Discord-Setup.exe' : 'EZArt-Discord.dmg';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setDownloadStarted(true);
        } else {
          // File doesn't exist or is HTML (404 page)
          alert(
            'Installer Not Available Yet\n\n' +
            'The Discord companion installer is being prepared.\n' +
            'For now, the integration works but requires manual setup.\n\n' +
            'Contact us to be notified when the installer is ready!'
          );
        }
      })
      .catch(() => {
        alert('Unable to download. Please contact support.');
      });

    // Show retry button briefly
    setTimeout(() => {
      setDownloadStarted(false);
    }, 3000);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-zinc-900/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-zinc-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-md p-6 m-4 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#5865F2]/10 flex items-center justify-center">
              <DiscordIcon className="text-[#5865F2]" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Discord Rich Presence</h2>
              <p className="text-sm text-zinc-400">Companion App Required</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors p-1 hover:bg-white/5 rounded"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <WarningCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-100">
              <p className="font-medium mb-1">Can't connect to companion app</p>
              <p className="text-blue-200/80">
                The Discord Rich Presence feature requires a small companion app running on your computer.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-white">What it does:</h3>
            <ul className="text-sm text-zinc-300 space-y-1.5">
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Shows what you're listening to on your Discord profile</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Let friends <b>Listen Along</b> with you in real-time</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Displays album artwork and track progress</span>
              </li>
            </ul>
          </div>

          {/* Platform Selection */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-white">Select your platform:</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPlatform('windows')}
                className={`p-3 rounded-lg border transition-all ${platform === 'windows'
                  ? 'bg-indigo-500/20 border-indigo-500 text-white'
                  : 'bg-zinc-800 border-white/10 text-zinc-400 hover:border-white/20'
                  }`}
              >
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M1 1h10v10H1V1zm12 0h10v10H13V1zm-12 12h10v10H1V13zm12 0h10v10H13V13z" />
                  </svg>
                </div>
                <div className="text-sm font-medium">Windows</div>
              </button>
              <button
                onClick={() => setPlatform('mac')}
                className={`p-3 rounded-lg border transition-all ${platform === 'mac'
                  ? 'bg-indigo-500/20 border-indigo-500 text-white'
                  : 'bg-zinc-800 border-white/10 text-zinc-400 hover:border-white/20'
                  }`}
              >
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                </div>
                <div className="text-sm font-medium">macOS</div>
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={handleDownload}
            className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <DownloadIcon className="w-5 h-5" />
            Download for {platform === 'windows' ? 'Windows' : 'macOS'}
          </button>

          {downloadStarted && (
            <button
              onClick={() => {
                onRetryConnection();
                onClose();
              }}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              I've installed it - Connect now
            </button>
          )}

          <button
            onClick={onClose}
            className="w-full text-zinc-400 hover:text-white font-medium py-2 transition-colors text-sm"
          >
            Maybe later
          </button>
        </div>

        {/* Setup Instructions */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <details className="text-sm">
            <summary className="text-zinc-400 hover:text-white cursor-pointer font-medium">
              📖 Setup Instructions
            </summary>
            <ol className="mt-3 space-y-2 text-zinc-300 list-decimal list-inside text-xs leading-relaxed">
              <li><strong>Run the installer</strong> - Follow the installation wizard</li>
              <li><strong>Open Discord</strong> - Desktop app must be running</li>
              <li><strong>Find the app</strong> - Look for the icon in your system tray (Windows) or menu bar (Mac)</li>
              <li><strong>Click Discord button</strong> - In EZ Art music player (turns blue when connected)</li>
              <li><strong>Play music</strong> - Your status updates automatically! 🎵</li>
            </ol>
            <p className="mt-3 text-xs text-zinc-400 italic">
              The app runs in the background and updates your Discord status in real-time.
            </p>
          </details>
        </div>
      </div>
    </div>
  );
};
